import PropTypes from 'prop-types';
import React, { lazy, Suspense } from 'react';
import { ChatbotProvider } from '../../../hooks/ChatbotContext';
import './BaseChatbotWidget.scss';

// Lazy load the heavy UI component - non-AI users don't pay bundle/runtime cost
const ChatbotWidgetCore = lazy(() => import('./ChatbotWidgetCore'));

/**
 * Chatbot widget that provides Context wrapper for the chatbot functionality
 *
 * Architecture:
 * - Provider always mounts (context available for hooks)
 * - UI only loads when AI is enabled (lazy loaded)
 * - When isAIEnabled=false: context exposes no-op actions, zero async work
 */
function ChatbotWidget({ initialState, isAIEnabled = true, ...props }) {
  return (
    <ChatbotProvider initialState={initialState} isAIEnabled={isAIEnabled}>
      {/* Mount UI when AI is enabled - hideFloatingButton only controls button visibility, not UI mounting */}
      {/* ChatbotWidgetCore handles hideFloatingButton internally to hide the floating trigger button */}
      {/* This allows chatbot to be opened programmatically via openChatbot() even when button is hidden */}
      {isAIEnabled && (
        <Suspense fallback={null}>
          <ChatbotWidgetCore {...props} />
        </Suspense>
      )}
      {!isAIEnabled && props.children}
    </ChatbotProvider>
  );
}

ChatbotWidget.propTypes = {
  /** Whether AI features are enabled (controls provider behavior and UI loading) */
  isAIEnabled: PropTypes.bool,
  chatService: PropTypes.object,
  emptyStateTitle: PropTypes.string,
  emptyStateText: PropTypes.string,
  historyButtonLabel: PropTypes.string,
  buttonIconName: PropTypes.string,
  errorMessage: PropTypes.string,
  className: PropTypes.string,
  children: PropTypes.node,
  getChatContextData: PropTypes.func,
  animationDuration: PropTypes.number,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
  isControlled: PropTypes.bool,
  isOpenControlled: PropTypes.bool,
  focusOnOpen: PropTypes.bool,
  practicePageUrls: PropTypes.array,
  initialState: PropTypes.object,
  hideFloatingButton: PropTypes.bool,
};

ChatbotWidget.defaultProps = {
  isAIEnabled: true,
  emptyStateTitle: 'AI Assistant',
  emptyStateText: 'Ask me anything about your course, lessons, or any other questions you might have.',
  historyButtonLabel: '📜 History',
  buttonIconName: 'chat',
  errorMessage: 'An error occurred. Please try again.',
  className: '',
  chatService: null,
  children: null,
  animationDuration: 500,
  onOpen: null,
  onClose: null,
  isControlled: false,
  isOpenControlled: undefined,
  focusOnOpen: true,
  initialState: null,
};

export default ChatbotWidget;
